import Loadable from 'react-loadable';
import { preloadApi } from '../../utils/preloadApi';
import { AuthenticationLoginPreload } from '../authentication/AuthenticationLoginPreload';
import { defaultLoadableConfig } from '../common';
import { CommunitiesListLoadingPlaceholder } from '../communities/list/CommunitiesListLoadingPlaceholder';
import { ListLoadingPlaceholder } from '../list/listPage/ListLoadingPlaceholder';
import { NewsListLoadingPlaceholder } from '../news/list/NewsListLoadingPlaceholder';
import { PublicNewsListLoadingPlaceholder } from '../publicNews/list/PublicNewsListLoadingPlaceholder';
import { TimecardListLoadingPlaceholder } from '../timecard/list/TimecardListLoadingPlaceholder';
import { DocumentListLoadingPlaceholder } from '../document/list/DocumentListLoadingPlaceholder';
import { GenericPublicListLoadingPlaceholder } from '../list/listPage/ListLoadingPlaceholder/GenericPublicListLoadingPlaceholder';
import { PublicDocumentListLoadingPlaceholder } from '../publicDocument/list/PublicDocumentListLoadingPlaceholder';
import { AllJobsListLoadingPlaceholder } from '../publicJob/allJobs/AllJobsListLoadingPlaceholder';
import { CommentsListLoadingPlaceholder } from '../comment/list/CommentsListLoadingPlaceholder';
import { SurveyListLoadingPlaceholder } from '../survey/list/SurveyListLoadingPlaceholder';
import { TaskListLoadingPlaceholder } from '../activity/activityTaskList/TaskListLoadingPlaceholder';
import { ManagementInformationLoadingPlaceholder } from '../managementInformation/components/ManagementInformationPlaceholder/ManagementInformationLoadingPlaceholder';
import { BackofficeActionPagePlaceholder } from '../backoffice/BackofficeActionPagePlaceholder';
import { DetailPagePlaceholder } from '../detailPage/components/DetailPagePlaceholder/DetailPagePlaceholder';

export const UserListEntry = Loadable({
	loader: () => import('../user/list/UserList').then((i) => i.UserListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={4}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const PayslipsListEntry = Loadable({
	loader: () => import('../payslips/list/PayslipsList').then((m) => m.PayslipsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3045}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const JobBoardListEntry = Loadable({
	loader: () => import('../jobBoard/list/JobBoardList').then((m) => m.JobBoardListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={9019}
			columnsCount={5}
		/>
	),
});

export const JobBoardDetailEntry = Loadable({
	loader: () => import('../jobBoard/detail/JobBoardDetail').then((m) => m.JobBoardDetailWithProviders),
	beforeLoad: () =>
		preloadApi('/jobBoard/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const JobBoardInstanceListEntry = Loadable({
	loader: () =>
		import('../jobBoardInstance/list/JobBoardInstanceList').then((m) => m.JobBoardInstanceListWithProviders),
	loading: () => <ListLoadingPlaceholder titleCode={9018} />,
});

export const DocumentTemplateListEntry = Loadable({
	loader: () =>
		import('../documentTemplate/list/DocumentTemplateList').then((m) => m.DocumentTemplateListWithProviders),
	loading: () => <ListLoadingPlaceholder titleCode={6441} />,
});

export const CandidatePensionListEntry = Loadable({
	loader: () =>
		import('../candidatePension/list/CandidatePensionList').then(
			(module) => module.CandidatePensionListWithProviders,
		),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={10202}
			columnsCount={8}
			hasColumnSwitcher
			skipSearchField
			hasMultiselect
		/>
	),
});

export const FormsListEntry = Loadable({
	loader: () => import('../forms/list/FormsList').then((m) => m.FormsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5287}
			columnsCount={8}
			hasColumnSwitcher
		/>
	),
});

export const FormsPublicEntry = Loadable({
	loader: () => import('../forms/public/FormsPublic').then((m) => m.FormsPublic),
	...defaultLoadableConfig,
});

export const ActivityListEntry = Loadable({
	loader: () => import('../activity/activityList/ActivityList').then((m) => m.ActivityListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={6537}
			columnsCount={8}
			hasMultiselect
		/>
	),
});

export const TaskListEntry = Loadable({
	loader: () => import('../activity/activityTaskList/TaskList').then((m) => m.TaskListWithProviders),
	loading: () => <TaskListLoadingPlaceholder />,
});

export const ApiUserDetailEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../apiuser/detail/ApiUserDetail').then((m) => m.ApiUserDetail),
	beforeLoad: () =>
		preloadApi('/apiUser/commonDetailsJson', {
			includeRouteId: true,
		}),
});

export const ApiUserListEntry = Loadable({
	loader: () => import('../apiuser/list/ApiUserList').then((m) => m.ApiUserListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={9202}
			columnsCount={7}
		/>
	),
});

export const AuditTrailListEntry = Loadable({
	loader: () => import('../auditTrail/list/AuditTrailListWithProviders').then((m) => m.AuditTrailListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={380}
			columnsCount={7}
			hasColumnSwitcher
			skipTotalItemsCount
			skipSearchField
			skipNavigation
		/>
	),
});

export const CandidateChangeListEntry = Loadable({
	loader: () =>
		import('../candidateChange/list/CandidateChangeList').then((module) => module.CandidateChangeListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5156}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const CaoListEntry = Loadable({
	loader: () => import('../cao/list/CaoList').then((module) => module.CaoListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1773}
			columnsCount={5}
		/>
	),
});

export const CaoInstanceListEntry = Loadable({
	loader: () => import('../caoInstance/list/CaoInstanceList').then((module) => module.CaoInstanceListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={12379}
			hasColumnSwitcher
			columnsCount={5}
		/>
	),
});

export const CaoNewsDetailEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../caoNews/detail/CaoNewsDetail').then((m) => m.CaoNewsDetail),
	beforeLoad: () =>
		preloadApi('/caoNews/detailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
});

export const CaoNewsListEntry = Loadable({
	loader: () => import('../caoNews/list/CaoNewsList').then((m) => m.CaoNewsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1694}
			skipTotalItemsCount
		/>
	),
});

export const CaoWagesListEntry = Loadable({
	loader: () => import('../caoWages/list/CaoWagesList').then((m) => m.CaoWagesListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={6204}
			columnsCount={5}
			skipTotalItemsCount
			skipSearchField
		/>
	),
});

export const CaoWagesRequestDetailEntry = Loadable({
	loader: () => import('../caoWagesRequest/detail/CaoWagesRequestDetail').then((m) => m.CaoWagesRequestDetail),
	beforeLoad: () =>
		preloadApi('/caoWagesRequest/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const CaoWagesRequestListEntry = Loadable({
	loader: () =>
		import('../caoWagesRequest/list/CaoWagesRequestList').then((module) => module.CaoWagesRequestListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={6215}
			hasColumnSwitcher
			columnsCount={10}
		/>
	),
});

export const ClockwiseDetailEntry = Loadable({
	loader: () => import('modules/clockwise/detail/ClockwiseDetail'),
	beforeLoad: () =>
		preloadApi('/timecard/commonDetailsData', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
	...defaultLoadableConfig,
});

export const CommentsListEntry = Loadable({
	loader: () => import('../comment/list/CommentsList').then((m) => m.CommentsListWithProviders),
	loading: () => <CommentsListLoadingPlaceholder />,
});

export const CommunitiesDetailEntry = Loadable({
	loader: () => import('../communities/detail/CommunitiesDetail'),
	beforeLoad: () =>
		preloadApi('/community/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const CommunitiesListEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../communities/list/CommunitiesList').then((m) => m.CommunitiesListWithProviders),
	loading: () => <CommunitiesListLoadingPlaceholder />,
});

export const ConnectSettingsDetailEntry = Loadable({
	loader: () => import('../connect/settings/ConnectSettingsDetail').then((m) => m.ConnectSettingsDetail),
	beforeLoad: () => preloadApi('/connect/settingsJson'),
	...defaultLoadableConfig,
});

export const ContactPersonDetailEntry = Loadable({
	loader: () => import('../contactPersons/detail/ContactPersonDetail').then((m) => m.ContactPersonDetail),
	beforeLoad: () =>
		preloadApi('/employerContactPerson/CommonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
	loading: () => <DetailPagePlaceholder />,
});

export const ContactPersonListEntry = Loadable({
	loader: () => import('../contactPersons/list/ContactPersonList').then((m) => m.ContactPersonListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={11285}
			hasColumnSwitcher
			columnsCount={5}
			hasMultiselect
		/>
	),
});

export const ContractsDetailEntry = Loadable({
	loader: () => import('../contracts/detail/ContractsDetail').then((m) => m.ContractsDetail),
	beforeLoad: () =>
		preloadApi('/contract/commonDetailsJson', {
			includeParamsFromUrl: ['organizationId'],
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ContractsListEntry = Loadable({
	loader: () => import('../contracts/list/ContractsList').then((m) => m.ContractsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={316}
			hasColumnSwitcher
			columnsCount={5}
			hasMultiselect
		/>
	),
});

export const CustomerInvoiceDetailEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../customerInvoice/detail/CustomerInvoiceDetail').then((m) => m.CustomerInvoiceDetail),
	beforeLoad: () =>
		preloadApi('/customerInvoice/commonDetailsJson', {
			includeRouteId: true,
		}),
});

export const CustomerSubscriptionsDetailEntry = Loadable({
	loader: () =>
		import('../customerSubscriptions/detail/CustomerSubscriptionsDetail').then(
			(m) => m.CustomerSubscriptionsDetail,
		),
	beforeLoad: () =>
		preloadApi('/customerSubscriptions/commonDetailsJson', {
			includeParamsFromUrl: ['customerId', 'subscriptionId'],
		}),
	...defaultLoadableConfig,
});

export const SubscriptionSettingDetailEntry = Loadable({
	loader: () =>
		import('../subscriptionSetting/detail/SubscriptionSettingDetail').then((m) => m.SubscriptionSettingDetail),
	beforeLoad: () =>
		preloadApi('/subscriptionSetting/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ContractsUpdateDetailEntry = Loadable({
	loader: () => import('../contractsUpdate/detail/ContractsUpdateDetail').then((m) => m.ContractsUpdateDetail),
	beforeLoad: () =>
		preloadApi('/contract/BulkUpdateRequestCommonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ContractsUpdateListEntry = Loadable({
	loader: () => import('../contractsUpdate/list/ContractsUpdateList').then((m) => m.ContractsUpdateListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			skipCollapse
			titleCode={14129}
		/>
	),
});

export const CaoCustomAgreementListEntry = Loadable({
	loader: () => import('../customCao/list/CaoCustomAgreementList').then((m) => m.CaoCustomAgreementListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={13947}
			columnsCount={7}
		/>
	),
});

export const CustomCaoInstanceListEntry = Loadable({
	loader: () =>
		import('../customCaoInstance/list/CustomCaoInstanceList').then((m) => m.CustomCaoInstanceListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={14103}
			columnsCount={4}
			skipCollapse
		/>
	),
});

export const CustomerDetailEntry = Loadable({
	loader: () => import('../customer/detail/CustomerDetail').then((m) => m.CustomerDetail),
	beforeLoad: () =>
		preloadApi('/customer/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const CustomerListEntry = Loadable({
	loader: () => import('../customer/list/CustomerList').then((m) => m.CustomerListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={785}
			columnsCount={8}
			hasColumnSwitcher
			hasAvatar
			skipCollapse
		/>
	),
});

export const CustomerInvoiceListEntry = Loadable({
	loader: () => import('../customerInvoice/list/CustomerInvoiceList').then((m) => m.CustomerInvoiceListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={8861}
			columnsCount={2}
			hasMultiselect
			skipCollapse
			skipSearchField
		/>
	),
});

export const CustomerInvoiceComponentListEntry = Loadable({
	loader: () =>
		import('../customerInvoiceComponent/list/CustomerInvoiceComponentList').then(
			(m) => m.CustomerInvoiceComponentListWithProviders,
		),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3590}
			columnsCount={7}
			hasColumnSwitcher
			skipNavigation
			skipSearchField
			hasMultiselect
		/>
	),
});

export const CustomerSubscriptionsListEntry = Loadable({
	loader: () =>
		import('../customerSubscriptions/list/CustomerSubscriptionsList').then(
			(m) => m.CustomerSubscriptionsListWithProviders,
		),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={9301}
			columnsCount={9}
			hasMultiselect
			skipSearchField
		/>
	),
});

export const DashboardEntry = Loadable({
	loader: () => import('modules/dashboard').then((m) => m.Dashboard),
	...defaultLoadableConfig,
});

export const DocumentListEntry = Loadable({
	loader: () => import('../document/list/DocumentList').then((m) => m.DocumentListWithProviders),
	loading: () => <DocumentListLoadingPlaceholder />,
});

export const DocumentSettingsDetailEntry = Loadable({
	loader: () => import('../documentSettings/detail/DocumentSettingsDetail').then((m) => m.DocumentSettingsDetail),
	beforeLoad: () =>
		preloadApi('/document/documentSettingCommonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const DocumentSettingsListEntry = Loadable({
	loader: () =>
		import('../documentSettings/list/DocumentSettingsList').then((m) => m.DocumentSettingsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1555}
			hasMultiselect
			skipCollapse
		/>
	),
});

export const DocumentTemplateDetailEntry = Loadable({
	loader: () => import('../documentTemplate/detail/DocumentTemplateDetail'),
	beforeLoad: () =>
		preloadApi('/documentTemplate/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
	...defaultLoadableConfig,
});

export const EmailSenderAuthDetailEntry = Loadable({
	loader: () => import('../emailSenderAuth/detail/EmailSenderAuthDetail'),
	beforeLoad: () =>
		preloadApi('/emailSenderAuth/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const EmailSenderAuthListEntry = Loadable({
	loader: () => import('../emailSenderAuth/list/EmailSenderAuthList').then((m) => m.EmailSenderAuthListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={10403}
			hasMultiselect
			skipCollapse
			columnsCount={4}
		/>
	),
});

export const PageDetailEntry = Loadable({
	loader: () => import('../page/detail/PageDetail').then((m) => m.PageDetail),
	beforeLoad: () =>
		preloadApi('/page/commonDetailsJson', {
			includeParamsFromUrl: ['path', 'labelId'],
		}),
	...defaultLoadableConfig,
});

export const FormsDetailEntry = Loadable({
	loader: () => import('../forms/detail/FormsDetail').then((m) => m.FormsDetail),
	beforeLoad: () =>
		preloadApi('/form/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const FsApiLogListEntry = Loadable({
	loader: () => import('../fsApiLog/list/FsApiLogListWithProviders').then((m) => m.FsApiLogListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5222}
			skipCollapse
			skipNavigation
			columnsCount={9}
		/>
	),
});

export const IllnessDetailEntry = Loadable({
	loader: () => import('../illness/detail/IllnessDetailWithProviders').then((m) => m.IllnessDetailWithProviders),
	beforeLoad: () =>
		preloadApi('/illness/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const IllnessListEntry = Loadable({
	loader: () => import('../illness/list/IllnessList').then((m) => m.IllnessListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3431}
			columnsCount={7}
			hasColumnSwitcher
		/>
	),
});

export const IncomingMessageDetailEntry = Loadable({
	loader: () => import('../incomingMessage/detail/IncomingMessageDetail').then((m) => m.IncomingMessageDetail), // api cannot be properly preloaded
	...defaultLoadableConfig,
});

export const IncomingMessageListEntry = Loadable({
	loader: () => import('../incomingMessage/list/IncomingMessageList').then((m) => m.IncomingMessageListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={2463}
			columnsCount={5}
			skipCollapse
		/>
	),
});

export const IncomingMessageLogListEntry = Loadable({
	loader: () =>
		import('../incomingMessage/logList/IncomingMessageLogList').then((m) => m.IncomingMessageListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={821}
			columnsCount={5}
			skipCollapse
			skipNavigation
			skipTotalItemsCount
		/>
	),
});

export const InvoiceListEntry = Loadable({
	loader: () => import('../invoice/list/InvoiceList').then((m) => m.InvoiceListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3009}
			skipNavigation
			hasMultiselect
		/>
	),
});

export const PurchaseInvoiceListEntry = Loadable({
	loader: () => import('../purchaseInvoice/list/PurchaseInvoiceList').then((m) => m.PurchaseInvoiceListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={14504}
			skipNavigation
			skipCollapse
			hasMultiselect
		/>
	),
});

export const JobDetailEntry = Loadable({
	...defaultLoadableConfig,
	beforeLoad: () =>
		preloadApi('/job/commonDetailsJson', {
			includeRouteId: true,
		}),
	loader: () => import('modules/job/detail/JobDetail'),
});

export const JobListEntry = Loadable({
	loader: () => import('../job/list/JobList').then((m) => m.JobsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1397}
			columnsCount={8}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const JobPostListEntry = Loadable({
	loader: () => import('../jobPost/list/JobPostList').then((m) => m.JobPostListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={7196}
			columnsCount={7}
			hasMultiselect
			hasColumnSwitcher
			skipSearchField
		/>
	),
});

export const JobPostDetailEntry = Loadable({
	...defaultLoadableConfig,
	beforeLoad: () =>
		preloadApi('/jobPost/commonDetailsJson', {
			includeRouteId: true,
		}),
	loader: () => import('../jobPost/detail/JobPostDetail').then((m) => m.JobPostDetail),
});

export const LabelListEntry = Loadable({
	loader: () => import('../label/list/LabelList').then((m) => m.LabelListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5429}
			columnsCount={4}
			hasAvatar
			skipCollapse
		/>
	),
});

export const ApplicationsDetailEntry = Loadable({
	loader: () => import('../newApplications/detail/ApplicationsDetail'),
	beforeLoad: () =>
		preloadApi('/application/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ApplicationsListEntry = Loadable({
	loader: () => import('../newApplications/list/ApplicationsList').then((m) => m.ApplicationsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={4120}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const BureauDetailEntry = Loadable({
	loader: () => import('../newBureaus/detail/BureauDetail'),
	beforeLoad: () =>
		preloadApi('/agency/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const BureauListEntry = Loadable({
	loader: () => import('../newBureaus/list/BureauList').then((m) => m.BureauListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3157}
			hasColumnSwitcher
		/>
	),
});

export const CandidateDetailEntry = Loadable({
	loader: () => import('../newCandidate/detail/CandidateDetail').then((m) => m.CandidateDetail),
	beforeLoad: () =>
		preloadApi('/candidate/commonDetailsJson', {
			includeRouteId: true,
		}),
	loading: () => <DetailPagePlaceholder />,
});

export const CandidateListEntry = Loadable({
	loader: () => import('../newCandidate/list/CandidateList').then((m) => m.CandidateListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={14}
			columnsCount={4}
			hasMultiselect
			hasColumnSwitcher
		/>
	),
});

export const EmployerDetailEntry = Loadable({
	loader: () => import('../newEmployers/detail/EmployerDetail').then((m) => m.EmployerDetail),
	beforeLoad: () =>
		preloadApi('/employer/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const EmployerListEntry = Loadable({
	loader: () => import('../newEmployers/list/EmployerListWithProviders').then((m) => m.EmployerListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={6}
			columnsCount={5}
			hasColumnSwitcher
		/>
	),
});

export const NewsDetailEntry = Loadable({
	loader: () => import('../news/detail/NewsDetail'),
	beforeLoad: () =>
		preloadApi('/news/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const NewsAdminDetailEntry = Loadable({
	loader: () => import('../news/detailAdmin/NewsAdminDetail').then((m) => m.NewsAdminDetail),
	beforeLoad: () =>
		preloadApi('/newsAdmin/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const NewsListEntry = Loadable({
	loader: () => import('../news/list/NewsList').then((m) => m.NewsListWithProviders),
	loading: () => <NewsListLoadingPlaceholder />,
});

export const OutgoingMessageDetailEntry = Loadable({
	loader: () => import('../outgoingMessage/detail/OutgoingMessageDetail').then((m) => m.OutgoingMessageDetail), //api cannot be properly preloaded
	...defaultLoadableConfig,
});

export const OutgoingMessageListEntry = Loadable({
	loader: () => import('../outgoingMessage/list/OutgoingMessageList').then((m) => m.OutgoingMessageListWithProviders),
	loading: () => <ListLoadingPlaceholder titleCode={2464} />,
});

export const OutgoingMessageLogListEntry = Loadable({
	loader: () =>
		import('../outgoingMessage/logList/OutgoingMessageLogList').then((m) => m.OutgoingMessageListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			columnsCount={5}
			titleCode={2464}
			skipTotalItemsCount
		/>
	),
});

export const PageListEntry = Loadable({
	loader: () => import('../page/list/PageList').then((m) => m.PageListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			columnsCount={4}
			titleCode={257}
			skipCollapse
		/>
	),
});

export const TemplateListEntry = Loadable({
	loader: () => import('../templates/list/TemplateList').then((m) => m.LabelListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1363}
			columnsCount={5}
			skipCollapse
		/>
	),
});

export const ComponentModelDetailEntry = Loadable({
	loader: () => import('../componentModel/detail/ComponentModelDetail').then((m) => m.ComponentModelDetail),
	beforeLoad: () =>
		preloadApi('/componentModel/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ComponentModelListEntry = Loadable({
	loader: () => import('../componentModel/list/ComponentModelList').then((m) => m.ComponentModelListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5495}
			columnsCount={5}
			skipCollapse
			hasColumnSwitcher
		/>
	),
});

export const KeyIndicatorsEntry = Loadable({
	loader: () =>
		import('../connect/keyIndicators/list/KeyIndicatorsList').then((m) => m.KeyIndicatorsListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={9229}
			columnsCount={9}
			hasColumnSwitcher
			hasAvatar
			skipNavigation
			skipSearchField
		/>
	),
});

export const LabelDetailEntry = Loadable({
	loader: () => import('../label/detail/LabelDetail').then((m) => m.LabelDetail),
	beforeLoad: () =>
		preloadApi('/label/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ModelMatrixDetailEntry = Loadable({
	loader: () => import('../modelMatrix/detail/ModelMatrixDetail').then((m) => m.ModelMatrixDetail),
	beforeLoad: () =>
		preloadApi('/modelMatrix/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const ModelMatrixEntry = Loadable({
	loader: () => import('../modelMatrix/list/ModelMatrixList').then((m) => m.ModelMatrixWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={8101}
			columnsCount={7}
		/>
	),
});

export const ModelMatrixInstanceDetailEntry = Loadable({
	...defaultLoadableConfig,
	loader: () =>
		import('../modelMatrixInstance/detail/ModelMatrixInstanceDetail').then((m) => m.ModelMatrixInstanceDetail),
	beforeLoad: () =>
		preloadApi('/modelMatrixInstance/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
});

export const ModelMatrixInstanceEntry = Loadable({
	loader: () =>
		import('../modelMatrixInstance/list/ModelMatrixInstanceList').then((m) => m.ModelMatrixInstanceWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={206}
			columnsCount={7}
		/>
	),
});

export const NewsAdminListEntry = Loadable({
	loader: () => import('../news/listAdmin/NewsAdminList').then((m) => m.NewsAdminListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1424}
			skipCollapse
		/>
	),
});

export const RateClassEntry = Loadable({
	loader: () => import('../rateClass/list/RateClassList').then((m) => m.RateClassListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={6806}
			columnsCount={7}
			hasColumnSwitcher
			hasMultiselect
		/>
	),
});

export const SavedFilterAdminListEntry = Loadable({
	loader: () =>
		import('../savedFilter/adminList/SavedFilterAdminList').then((m) => m.SavedFilterAdminListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1126}
			columnsCount={5}
			hasColumnSwitcher
			skipSearchField
		/>
	),
});

export const SettingEntry = Loadable({
	loader: () => import('../setting/list/SettingList').then((m) => m.SettingListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={738}
			columnsCount={7}
			skipNavigation
			skipTotalItemsCount
			skipSearchField
		/>
	),
});

export const SubscriberListEntry = Loadable({
	loader: () => import('../subscriber/list/SubscriberList').then((m) => m.SubscriberListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={179}
			skipNavigation
		/>
	),
});

export const TextModelListEntry = Loadable({
	loader: () => import('../textModels/list/TextModelList').then((m) => m.TextModelListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={693}
			columnsCount={5}
			hasColumnSwitcher
			skipCollapse
		/>
	),
});

export const SavedFilterMyListEntry = Loadable({
	loader: () => import('../savedFilter/myList/SavedFilterMyList').then((m) => m.SavedFilterMyListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1125}
			columnsCount={4}
			skipCollapse
			skipSearchField
		/>
	),
});

export const SavedFilterOwnListEntry = Loadable({
	loader: () => import('../savedFilter/ownList/SavedFilterOwnList').then((m) => m.SavedFilterOwnListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1127}
			columnsCount={5}
			skipSearchField
		/>
	),
});

export const SubscriptionSettingListEntry = Loadable({
	loader: () =>
		import('../subscriptionSetting/list/SubscriptionSettingList').then(
			(m) => m.SubscriptionSettingListWithProviders,
		),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={5117}
			columnsCount={7}
			skipFilters
		/>
	),
});

export const SupplierListEntry = Loadable({
	loader: () => import('../supplier/list/SupplierList').then((m) => m.SupplierListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={12487}
			columnsCount={5}
			skipNavigation
		/>
	),
});

export const SupportAccessListEntry = Loadable({
	loader: () => import('../supportAccess/list/SupportAccessList').then((m) => m.SupportAccessListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={11416}
			columnsCount={7}
			hasColumnSwitcher
			hasMultiselect
			skipSearchField
		/>
	),
});

export const SurveyDetailEntry = Loadable({
	loader: () => import('../survey/detail/SurveyDetail').then((m) => m.SurveyDetail),
	beforeLoad: () =>
		preloadApi('/survey/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const SurveyListEntry = Loadable({
	loader: () => import('../survey/list/SurveyList').then((m) => m.SurveyListWithProviders),
	loading: () => <SurveyListLoadingPlaceholder />,
});

export const SurveyPublicEntry = Loadable({
	loader: () => import('../survey/public/SurveyPublic').then((m) => m.SurveyPublic),
	...defaultLoadableConfig,
});

export const SurveyAdminDetailEntry = Loadable({
	loader: () => import('../surveyAdmin/detail/SurveyAdminDetail').then((m) => m.SurveyAdminDetail),
	beforeLoad: () =>
		preloadApi('/surveyAdmin/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId', 'listData'],
		}),
	...defaultLoadableConfig,
});

export const SurveyAdminListEntry = Loadable({
	loader: () => import('../surveyAdmin/list/SurveyAdminList').then((m) => m.SurveyAdminListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={7409}
			hasColumnSwitcher
			skipTotalItemsCount
		/>
	),
});

export const OptOutListEntry = Loadable({
	beforeLoad: () => preloadApi('/surveyAdmin/ListOptOutJson'),
	loader: () => import('../surveyAdmin/optOut/list/OptOutList').then((m) => m.OptOutListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={7443}
			hasColumnSwitcher
			hasMultiselect
			skipNavigation
		/>
	),
});

export const TagListEntry = Loadable({
	loader: () => import('../tag/list/TagList').then((m) => m.TagListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={2399}
			hasMultiselect
			columnsCount={7}
		/>
	),
});

export const TemplateDetailEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../templates/detail/TemplateDetail').then((m) => m.TemplateDetail),
	beforeLoad: () =>
		preloadApi('/template/detailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['labelId'],
		}),
});

export const TextModelDetailEntry = Loadable({
	loader: () => import('modules/textModels/detail/TextModelDetail').then((m) => m.ConnectedTextModelDetail),
	beforeLoad: () =>
		preloadApi('/textModel/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const TimecardListEntry = Loadable({
	loader: () => import('../timecard/list/TimecardListWithProviders').then((m) => m.TimecardListWithProviders),
	loading: () => <TimecardListLoadingPlaceholder />,
});

export const TranslationDetailEntry = Loadable({
	loader: () => import('../translations/detail/TranslationDetail').then((m) => m.TranslationDetail),
	beforeLoad: () =>
		preloadApi('/translation/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['labelId', 'listData'],
		}),
	...defaultLoadableConfig,
});

export const TranslationListEntry = Loadable({
	loader: () => import('../translations/list/TranslationList').then((m) => m.TranslationListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={332}
			hasColumnSwitcher
			skipTotalItemsCount
		/>
	),
});

export const UserDetailEntry = Loadable({
	loader: () => import('../user/detail/UserDetail').then((m) => m.UserDetail),
	beforeLoad: () =>
		preloadApi('/user/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
	...defaultLoadableConfig,
});

export const UserProfileEntry = Loadable({
	loader: () => import('../user/userProfile/UserProfile').then((m) => m.UserProfileWithProviders),
	...defaultLoadableConfig,
});

export const WmlListEntry = Loadable({
	loader: () => import('../wml/list/WmlList').then((m) => m.WmlListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1605}
			columnsCount={5}
			skipTotalItemsCount
			skipCollapse
			skipFilters
			skipSearchField
		/>
	),
});

export const PaymentListEntry = Loadable({
	loader: () => import('../payment/list/PaymentList').then((m) => m.PaymentListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3413}
			hasMultiselect
			hasColumnSwitcher
			skipNavigation
			skipSearchField
		/>
	),
});

export const PayslipsDetailEntry = Loadable({
	loader: () => import('../payslips/detail/PayslipsDetail'),
	beforeLoad: () =>
		preloadApi('/payslip/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const PhaseListEntry = Loadable({
	loader: () => import('../phase/list/PhaseList').then((m) => m.PhaseListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3388}
			columnsCount={8}
			hasColumnSwitcher
			skipNavigation
			skipSearchField
		/>
	),
});

export const ProformaDetailEntry = Loadable({
	loader: () => import('../proforma/detail/ProformaDetail').then((m) => m.ProformaDetail),
	beforeLoad: () =>
		preloadApi('/proforma/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const ProformaListEntry = Loadable({
	loader: () => import('../proforma/list/ProformaList').then((m) => m.ProformaListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={12369}
			columnsCount={7}
		/>
	),
});

export const PublicDocumentAuthenticationEntry = Loadable({
	...defaultLoadableConfig,
	loader: () =>
		import('modules/publicDocument/authentication/PublicDocumentAuthentication').then(
			(m) => m.PublicDocumentAuthentication,
		),
});

export const PublicDocumentListEntry = Loadable({
	loader: () => import('../publicDocument/list/PublicDocumentList').then((m) => m.PublicDocumentListWithProviders),
	loading: () => <PublicDocumentListLoadingPlaceholder />,
});

export const RateClassDetailEntry = Loadable({
	loader: () => import('../rateClass/detail/RateClassDetail').then((m) => m.RateClassDetail),
	beforeLoad: () =>
		preloadApi('/rateClass/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const ReservationListEntry = Loadable({
	loader: () => import('../reservation/list/ReservationList').then((m) => m.ReservationListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={3162}
			hasMultiselect
			hasColumnSwitcher
			skipNavigation
			skipSearchField
		/>
	),
});

export const RolesDetailEntry = Loadable({
	loader: () => import('../roles/detail/RolesDetail').then((m) => m.RolesDetail),
	beforeLoad: () =>
		preloadApi('/role/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId', 'listData'],
		}),
	...defaultLoadableConfig,
});

export const RolesListEntry = Loadable({
	loader: () => import('../roles/list/RolesList').then((m) => m.RolesListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={2}
			columnsCount={5}
			hasMultiselect
			skipCollapse
		/>
	),
});

export const SafetyListEntry = Loadable({
	loader: () => import('../safety/list/SafetyList').then((m) => m.SafetyListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={11496}
			columnsCount={5}
			skipNavigation
			hasColumnSwitcher
		/>
	),
});

export const SavedFilterLogListEntry = Loadable({
	loader: () => import('../savedFilterLog/list/SavedFilterLogList').then((m) => m.SavedFilterLogListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={1131}
			columnsCount={5}
			skipNavigation
			skipTotalItemsCount
		/>
	),
});

export const ShareLogListEntry = Loadable({
	loader: () => import('../shareLog/list/ShareLogList').then((m) => m.ShareLogListWithProviders),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={9005}
			columnsCount={3}
			skipTotalItemsCount
			skipSearchField
			skipCollapse
		/>
	),
});

export const ShareLogDetailEntry = Loadable({
	loader: () => import('../shareLog/detail/ShareLogDetail').then((m) => m.ShareLogDetail),
	beforeLoad: () =>
		preloadApi('/shareLog/detailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['listData'],
		}),
	...defaultLoadableConfig,
});

export const DomainValuesListEntry = Loadable({
	loader: () => import('../domainValues/list/DomainValuesList').then((m) => m.DomainValuesListWithProvider),
	beforeLoad: () => preloadApi('/domainValue/listTypesJson'),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={7100}
			columnsCount={3}
			skipCollapse
		/>
	),
});

export const DomainValuesDetailEntry = Loadable({
	loader: () => import('../domainValues/detail/DomainValuesDetail').then((m) => m.DomainValuesDetail),
	beforeLoad: () =>
		preloadApi('/domainValue/commonDetailsJson', {
			includeRouteId: true,
			includeParamsFromUrl: ['organizationId'],
		}),
	...defaultLoadableConfig,
});

export const JobBoardInstanceDetailEntry = Loadable({
	loader: () => import('../jobBoardInstance/detail/JobBoardInstanceDetail').then((m) => m.JobBoardInstanceDetail),
	beforeLoad: () =>
		preloadApi('/jobBoardInstance/commonDetailsJson', {
			includeRouteId: true,
		}),
	...defaultLoadableConfig,
});

export const PlaygroundEntry = Loadable({
	loader: () => import('../Playground/Playground').then((m) => m.Playground),
	...defaultLoadableConfig,
});

export const DomainValueMappingEntry = Loadable({
	loader: () => import('../domainValues/mapping/Mapping').then((m) => m.Mapping),
	...defaultLoadableConfig,
});

export const DomainValueCaoMappingEntry = Loadable({
	...defaultLoadableConfig,
	loader: () => import('../domainValues/caoMapping/CaoMapping').then((m) => m.CaoMapping),
});

export const PublicJobListEntry = Loadable({
	loader: () => import('../publicJob/list/PublicJobList').then((m) => m.PublicJobListWithProviders),
	loading: () => (
		<GenericPublicListLoadingPlaceholder
			hasSidebar
			headerTitleCode={4212}
			findButtonTitleCode={4279}
			headerSearchFieldsCount={2}
		/>
	),
});

export const PublicEmployerListEntry = Loadable({
	loader: () => import('../publicEmployer/list/PublicEmployerList').then((m) => m.PublicEmployerListWithProviders),
	loading: () => (
		<GenericPublicListLoadingPlaceholder
			headerTitleCode={230}
			findButtonTitleCode={229}
		/>
	),
});

export const CustomPageMaintenanceEntry = Loadable({
	loader: () => import('../customPage/error/CustomPageMaintenance').then((m) => m.CustomPageMaintenance),
	...defaultLoadableConfig,
});

export const HomeEntry = Loadable({
	loader: () => import('../customPage/home/Home').then((m) => m.Home),
	...defaultLoadableConfig,
});

export const SecurityStatementEntry = Loadable({
	loader: () => import('../customPage/securityStatement/SecurityStatement').then((m) => m.SecurityStatement),
	...defaultLoadableConfig,
});

export const PrivacyStatementEntry = Loadable({
	loader: () => import('../customPage/privacyStatement/PrivacyStatement').then((m) => m.PrivacyStatement),
	...defaultLoadableConfig,
});

export const AboutUsEntry = Loadable({
	loader: () => import('../customPage/aboutUs/AboutUs').then((m) => m.AboutUs),
	...defaultLoadableConfig,
});

export const TermOfUseEntry = Loadable({
	loader: () => import('../customPage/termOfUse/TermOfUse').then((m) => m.TermOfUse),
	...defaultLoadableConfig,
});

export const CookiePolicyEntry = Loadable({
	loader: () => import('../customPage/cookiePolicy/CookiePolicy').then((m) => m.CookiePolicy),
	...defaultLoadableConfig,
});

export const CvdPolicyEntry = Loadable({
	loader: () => import('../customPage/cvdPolicy/CvdPolicy').then((m) => m.CvdPolicy),
	...defaultLoadableConfig,
});

export const IssueDetailEntry = Loadable({
	loader: () => import('../issue/detail/IssueDetail').then((m) => m.IssueDetail),
	beforeLoad: () => preloadApi('/issue/detailJson'),
	...defaultLoadableConfig,
});

export const ApplicationFormEntry = Loadable({
	loader: () => import('../newApplications/form/ApplicationForm').then((m) => m.ApplicationForm),
	...defaultLoadableConfig,
});

export const PublicJobAllJobsEntry = Loadable({
	loader: () => import('../publicJob/allJobs/AllJobs').then((m) => m.AllJobs),
	loading: () => <AllJobsListLoadingPlaceholder />,
});

export const PublicEmployerDetailEntry = Loadable({
	...defaultLoadableConfig,
	beforeLoad: () =>
		preloadApi('/publicEmployer/commonDetailsJson', {
			includeRouteId: true,
		}),
	loader: () => import('../publicEmployer/detail/PublicEmployerDetail').then((m) => m.PublicEmployerDetail),
});

export const PublicJobDetailEntry = Loadable({
	...defaultLoadableConfig,
	beforeLoad: () =>
		preloadApi('/publicJob/DetailsJson', {
			includeRouteId: true,
		}),
	loader: () => import('modules/publicJob/detail/PublicJobDetail').then((m) => m.PublicJobDetail),
});

export const PublicJobApplyEntry = Loadable({
	...defaultLoadableConfig,
	// beforeLoad: () => preloadApi('/publicJob/ApplyJson', {
	// 	includeRouteId: true,
	// 	includeParamsFromUrl: ['sourceJobBoardInstanceGuid']
	// }),
	loader: () => import('../publicJob/apply/PublicJobApply').then((m) => m.PublicJobApply),
});

export const PublicNewsListEntry = Loadable({
	loader: () => import('../publicNews/list/PublicNewsListWithProviders').then((m) => m.PublicNewsListWithProviders),
	loading: () => <PublicNewsListLoadingPlaceholder />,
});

export const PublicNewsDetailEntry = Loadable({
	...defaultLoadableConfig,
	beforeLoad: () =>
		preloadApi('/publicNews/commonDetailsJson', {
			includeRouteId: true,
		}),
	loader: () => import('../publicNews/detail/PublicNewsDetail').then((m) => m.PublicNewsDetail),
});

export const AuthenticationEntry = Loadable({
	loader: () => import('../authentication/AuthenticationLoginPage').then((m) => m.AuthenticationLoginPage),
	loading: () => <AuthenticationLoginPreload />,
});

export const SurveyPublicOptOutEntry = Loadable({
	loader: () => import('../survey/publicOptOut/SurveyPublicOptOut').then((m) => m.SurveyPublicOptOut),
	...defaultLoadableConfig,
});

export const CustomPageErrorEntry = Loadable({
	loader: () => import('../customPage/error/CustomPageError').then((m) => m.CustomPageError),
	...defaultLoadableConfig,
});

export const ManagementInformationEntry = Loadable({
	loader: () => import('../managementInformation/ManagementInformation').then((m) => m.ManagementInformation),
	loading: () => <ManagementInformationLoadingPlaceholder />,
});

export const BackofficeActionPageEntry = Loadable({
	loader: () => import('../backoffice/BackofficeActionPage').then((m) => m.BackofficeActionPage),
	loading: () => <BackofficeActionPagePlaceholder />,
});

export const JournalTransferFilesListEntry = Loadable({
	loader: () =>
		import('../journalTransferFiles/list/JournalTransferFilesList').then(
			(m) => m.JournalTransferFilesListWithProviders,
		),
	loading: () => (
		<ListLoadingPlaceholder
			titleCode={15780}
			hasColumnSwitcher
		/>
	),
});
